/**
 * Quest journal (KeyJ) — ACTIVE / COMPLETED tabs over the game. Each active
 * quest shows its title, the current stage's journal line, per-objective
 * progress ("text  k/n"), a READY badge when it's turn-in ready, and TRACK /
 * ABANDON controls (ABANDON asks for a confirming second click). Follows the
 * handlers-object panel pattern; styled inline (self-contained, like the NPC
 * dialog) so it needs no shared CSS.
 */
import type { QuestDef, QuestInstance } from "@hyperlight/shared";
import type { QuestLog } from "../rpg/quests";

export class JournalPanel {
  visible = false;
  private el: HTMLElement;
  private listEl: HTMLElement;
  private tabsEl: HTMLElement;
  private tab: "active" | "done" = "active";
  /** Quest id whose ABANDON is armed for the confirming second click. */
  private confirm: string | null = null;

  constructor(
    private quests: QuestLog,
    private handlers: {
      onIntent: (m: { op: "abandon"; q: string }) => void;
      onClose: () => void;
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "quest-journal";
    this.el.style.cssText = [
      "position:fixed",
      "left:50%",
      "top:50%",
      "transform:translate(-50%,-50%)",
      "width:560px",
      "max-width:92vw",
      "max-height:82vh",
      "box-sizing:border-box",
      "background:rgba(10,18,28,0.94)",
      "border:1px solid rgba(140,180,220,0.35)",
      "border-radius:6px",
      "padding:16px 18px",
      "color:#cfe8ff",
      "font-family:monospace",
      "display:none",
      "flex-direction:column",
      "z-index:42",
    ].join(";");
    this.el.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:10px">
        <b style="font-size:16px;letter-spacing:2px">QUEST JOURNAL</b>
        <span style="opacity:0.5;font-size:11px">J / ESC — close</span>
      </div>
      <div class="qj-tabs" style="display:flex;gap:8px;margin-bottom:10px"></div>
      <div class="qj-list" style="overflow-y:auto;flex:1;min-height:0;display:flex;flex-direction:column;gap:10px"></div>`;
    document.body.appendChild(this.el);
    this.tabsEl = this.el.querySelector(".qj-tabs")!;
    this.listEl = this.el.querySelector(".qj-list")!;
    this.tabsEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-tab]") as HTMLElement | null;
      if (!btn) return;
      this.tab = btn.dataset.tab as "active" | "done";
      this.confirm = null;
      this.render();
    });
    this.listEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-act]") as HTMLElement | null;
      if (!btn) return;
      e.stopPropagation();
      const q = btn.dataset.q!;
      if (btn.dataset.act === "track") {
        this.quests.track(q);
        this.confirm = null;
        this.render();
      } else if (btn.dataset.act === "abandon") {
        if (this.confirm === q) {
          this.handlers.onIntent({ op: "abandon", q });
          this.confirm = null;
        } else {
          this.confirm = q; // arm the confirming second click
        }
        this.render();
      }
    });
  }

  open(): void {
    this.visible = true;
    this.confirm = null;
    this.el.style.display = "flex";
    this.render();
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.confirm = null;
    this.el.style.display = "none";
    this.handlers.onClose();
  }

  toggle(): void {
    if (this.visible) this.close();
    else this.open();
  }

  private btnStyle(active: boolean): string {
    return (
      "background:" +
      (active ? "#1d3550" : "#16283c") +
      ";border:1px solid " +
      (active ? "#6fb0ff" : "#3f5c78") +
      ";color:#cfe8ff;font-family:monospace;padding:5px 12px;cursor:pointer;letter-spacing:1px;font-size:11px;border-radius:3px"
    );
  }

  /** Re-render from state — cheap; called on open + when quests.rev changes. */
  render(): void {
    if (!this.visible) return;
    const active = this.quests.act;
    const done = this.quests.done;
    this.tabsEl.innerHTML =
      `<button data-tab="active" style="${this.btnStyle(this.tab === "active")}">ACTIVE · ${active.length}</button>` +
      `<button data-tab="done" style="${this.btnStyle(this.tab === "done")}">COMPLETED · ${done.length}</button>`;

    if (this.tab === "active") {
      this.listEl.innerHTML =
        active.length === 0
          ? this.emptyRow("No active quests. Visit a settlement's quest board.")
          : active.map((inst) => this.activeCard(inst)).join("");
    } else {
      this.listEl.innerHTML =
        done.length === 0
          ? this.emptyRow("Nothing completed yet.")
          : done
              .slice()
              .reverse()
              .map((id) => this.doneCard(id))
              .join("");
    }
  }

  private emptyRow(text: string): string {
    return `<div style="opacity:0.5;font-size:12px;padding:14px 4px;text-align:center">${text}</div>`;
  }

  private cardStyle(): string {
    return "border:1px solid rgba(120,150,190,0.25);border-radius:5px;padding:10px 12px;background:rgba(20,32,48,0.5)";
  }

  private activeCard(inst: QuestInstance): string {
    const def = this.quests.defOf(inst);
    const title = def?.title ?? "QUEST";
    const ready = this.quests.ready(inst);
    const tracked = this.quests.tracked === inst.id;
    const cat = def?.category === "campaign" ? "CAMPAIGN" : "TOWN";
    const badge = ready
      ? `<span style="color:#8fe08f;font-size:11px;letter-spacing:1px">● READY</span>`
      : `<span style="opacity:0.45;font-size:10px;letter-spacing:1px">${cat}</span>`;

    let body: string;
    if (!def) {
      // Def unresolvable here (rare) — never claim completion for it.
      body = `<div style="font-size:12px;opacity:0.6;margin-top:6px">Objective details unavailable here.</div>`;
    } else if (ready) {
      body = `<div style="font-size:12px;color:#8fe08f;margin-top:6px">All objectives complete — turn in your reward.</div>`;
    } else {
      const stage = def.stages[inst.st];
      const journal = stage?.journal ?? "";
      const objs = (stage?.objectives ?? [])
        .map((o, oi) => {
          const k = inst.prog[oi] ?? 0;
          const met = k >= o.n;
          const mark = met ? "✔" : "▫";
          const count = o.n > 1 ? `<span style="opacity:0.7">${k}/${o.n}</span>` : "";
          return `<div style="display:flex;justify-content:space-between;gap:10px;font-size:12px;color:${met ? "#8fe08f" : "#cfe8ff"}">
            <span>${mark} ${o.text}</span>${count}</div>`;
        })
        .join("");
      body =
        `<div style="font-size:11px;opacity:0.6;margin:5px 0 6px;font-style:italic">${journal}</div>` +
        objs;
    }

    const trackLabel = tracked ? "TRACKED" : "TRACK";
    const abandonLabel = this.confirm === inst.id ? "CONFIRM?" : "ABANDON";
    const reward = def ? this.quests.rewardLine(def) : "";
    return `<div style="${this.cardStyle()}">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px">
        <b style="font-size:13px;letter-spacing:1px">${title.toUpperCase()}</b>${badge}
      </div>
      ${body}
      ${reward ? `<div style="font-size:11px;opacity:0.6;margin-top:6px">REWARD · ${reward}</div>` : ""}
      <div style="display:flex;gap:8px;margin-top:9px">
        <button data-act="track" data-q="${inst.id}" style="${this.btnStyle(tracked)}">${trackLabel}</button>
        <button data-act="abandon" data-q="${inst.id}" style="${this.btnStyle(false)};${
          this.confirm === inst.id ? "border-color:#e08f6f;color:#ffbfa0" : ""
        }">${abandonLabel}</button>
      </div>
    </div>`;
  }

  private doneCard(id: string): string {
    const def: QuestDef | null = this.quests.resolveDef(id);
    const title = def?.title ?? id;
    const reward = def ? this.quests.rewardLine(def) : "";
    return `<div style="${this.cardStyle()};opacity:0.7">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px">
        <b style="font-size:13px;letter-spacing:1px">${title.toUpperCase()}</b>
        <span style="color:#8fe08f;font-size:11px">✔ COMPLETE</span>
      </div>
      ${reward ? `<div style="font-size:11px;opacity:0.6;margin-top:5px">REWARD · ${reward}</div>` : ""}
    </div>`;
  }
}
